import { LEAD_INTEREST } from "../lib/enums";
import { LEAD_INTEREST_LABELS } from "../lib/labels";

export function getPublicDemoSubmitButtonLabel(submitting) {
  return submitting ? "Wird gesendet …" : "Anfrage senden";
}

function isValidPublicHttpUrl(value) {
  if (!value || typeof value !== "string") return false;
  return /^https?:\/\/\S+$/i.test(value.trim());
}

function isValidPublicContactEmail(value) {
  if (!value || typeof value !== "string") return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

const PUBLIC_FORM_FIELD_DEFAULTS = [
  {
    key: "name",
    type: "text",
    label: "Name",
    placeholder: "Ihr Name",
    required: true,
    visible: true,
    order: 10,
    maps_to: "contact_name",
    locked: true,
    options: [],
  },
  {
    key: "email",
    type: "email",
    label: "E-Mail",
    placeholder: "ihre.email@firma.de",
    required: true,
    visible: true,
    order: 20,
    maps_to: "contact_email",
    locked: true,
    options: [],
  },
  {
    key: "company",
    type: "text",
    label: "Firma",
    placeholder: "Unternehmen",
    required: false,
    visible: true,
    order: 30,
    maps_to: "company_name",
    locked: false,
    options: [],
  },
  {
    key: "phone",
    type: "tel",
    label: "Telefon",
    placeholder: "Optional für Rückfragen",
    required: false,
    visible: true,
    order: 40,
    maps_to: "contact_phone",
    locked: false,
    options: [],
  },
  {
    key: "interest",
    type: "select",
    label: "Was möchten Sie besprechen?",
    placeholder: "",
    required: false,
    visible: true,
    order: 50,
    maps_to: "interest",
    locked: false,
    options: Object.values(LEAD_INTEREST).map((value) => ({
      value,
      label: LEAD_INTEREST_LABELS[value],
    })),
  },
  {
    key: "message",
    type: "textarea",
    label: "Nachricht",
    placeholder: "Worum geht es in Ihrer Anfrage oder was möchten Sie besprechen?",
    required: false,
    visible: true,
    order: 60,
    maps_to: "description",
    locked: false,
    options: [],
  },
];

function trimToString(value, fallback = "", maxLength = 300) {
  const text = typeof value === "string" ? value.trim() : "";
  return (text || fallback).slice(0, maxLength);
}

function normalizeInterestOptions(options) {
  const defaultOptions = PUBLIC_FORM_FIELD_DEFAULTS.find((field) => field.key === "interest").options;
  if (!Array.isArray(options)) return defaultOptions;
  const seen = new Set();
  const normalized = options
    .filter((option) => option && typeof option === "object")
    .map((option) => ({
      value: typeof option.value === "string" ? option.value.trim() : "",
      label: trimToString(option.label, "", 120),
    }))
    .filter((option) => LEAD_INTEREST[option.value] || Object.values(LEAD_INTEREST).includes(option.value))
    .filter((option) => {
      if (!option.value || seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    })
    .map((option) => ({
      value: option.value,
      label: option.label || LEAD_INTEREST_LABELS[option.value],
    }));
  return normalized.length ? normalized : defaultOptions;
}

export function normalizePublicFormFieldConfig(fieldConfig = []) {
  const rawByKey = new Map();
  if (Array.isArray(fieldConfig)) {
    fieldConfig.forEach((field) => {
      if (!field || typeof field !== "object") return;
      const key = typeof field.key === "string" ? field.key.trim() : "";
      if (!PUBLIC_FORM_FIELD_DEFAULTS.some((defaultField) => defaultField.key === key)) return;
      if (!rawByKey.has(key)) rawByKey.set(key, field);
    });
  }

  const normalized = PUBLIC_FORM_FIELD_DEFAULTS.map((defaultField) => {
    const raw = rawByKey.get(defaultField.key) || {};
    const locked = Boolean(defaultField.locked);
    const visible = locked ? true : raw.visible ?? defaultField.visible;
    const required = locked ? true : visible ? raw.required ?? defaultField.required : false;
    return {
      ...defaultField,
      label: trimToString(raw.label, defaultField.label, 120),
      placeholder: trimToString(raw.placeholder, defaultField.placeholder, 300),
      visible: Boolean(visible),
      required: Boolean(required),
      order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : defaultField.order,
      options:
        defaultField.key === "interest"
          ? normalizeInterestOptions(raw.options)
          : [],
    };
  });

  const defaultIndex = Object.fromEntries(
    PUBLIC_FORM_FIELD_DEFAULTS.map((field, index) => [field.key, index])
  );

  return normalized.sort((a, b) => {
    if (a.order === b.order) {
      return defaultIndex[a.key] - defaultIndex[b.key];
    }
    return a.order - b.order;
  });
}

export function buildInitialPublicFormState(fieldConfig = []) {
  const normalized = normalizePublicFormFieldConfig(fieldConfig);
  const interestField = normalized.find((field) => field.key === "interest");
  return {
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
    interest: interestField?.options?.[0]?.value || LEAD_INTEREST.OPTIMES_DEMO,
    website: "",
    email_tracking_consent: false,
    marketing_consent: false,
  };
}

export function validatePublicFormValues(fieldConfig = [], values = {}) {
  const fields = normalizePublicFormFieldConfig(fieldConfig);
  const errors = {};

  fields.forEach((field) => {
    if (!field.visible) return;
    const rawValue = values[field.key];
    const value = typeof rawValue === "string" ? rawValue.trim() : "";

    if (field.required && !value) {
      errors[field.key] = "Bitte dieses Feld ausfüllen.";
      return;
    }

    if (field.type === "email" && value && !isValidPublicContactEmail(value)) {
      errors[field.key] = "Bitte eine gültige E-Mail-Adresse angeben.";
      return;
    }

    if (field.type === "select" && value) {
      const allowedValues = field.options.map((option) => option.value);
      if (!allowedValues.includes(value)) {
        errors[field.key] = "Bitte eine gültige Auswahl treffen.";
      }
    }
  });

  return errors;
}

export function maybeReorderFieldConfig(fieldConfig = [], key, direction) {
  const fields = normalizePublicFormFieldConfig(fieldConfig);
  const currentIndex = fields.findIndex((field) => field.key === key);
  if (currentIndex < 0) return fields;
  if (fields[currentIndex].locked) return fields;
  const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
  if (targetIndex < 0 || targetIndex >= fields.length) return fields;
  if (fields[targetIndex].locked) return fields;

  const reordered = [...fields];
  [reordered[currentIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[currentIndex]];
  return reordered.map((field, index) => ({
    ...field,
    order: (index + 1) * 10,
  }));
}

export function buildPrivacyDisclaimerParts(text, { contactEmail, privacyPolicyUrl } = {}) {
  const sourceText = text || "";
  const parts = [];
  const tokenRegex = /(E-Mail|Datenschutzerklärung)/g;
  let lastIndex = 0;

  sourceText.replace(tokenRegex, (match, _token, offset) => {
    if (offset > lastIndex) {
      parts.push({ type: "text", text: sourceText.slice(lastIndex, offset) });
    }

    if (match === "E-Mail" && isValidPublicContactEmail(contactEmail)) {
      parts.push({ type: "link", text: match, href: `mailto:${contactEmail.trim()}` });
    } else if (
      match === "Datenschutzerklärung" &&
      isValidPublicHttpUrl(privacyPolicyUrl)
    ) {
      parts.push({ type: "link", text: match, href: privacyPolicyUrl.trim() });
    } else {
      parts.push({ type: "text", text: match });
    }

    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < sourceText.length) {
    parts.push({ type: "text", text: sourceText.slice(lastIndex) });
  }

  return parts.length ? parts : [{ type: "text", text: sourceText }];
}

export function buildPublicDemoPageContent(project, publicForm = null, tenantInfo = null) {
  const safeProject = project || "OptiMES Prime";
  const fieldConfig = normalizePublicFormFieldConfig(publicForm?.field_config);
  const headline = publicForm?.headline?.trim() || `Sprechen wir über Ihr ${safeProject}-Projekt.`;
  const subline =
    publicForm?.subline?.trim() ||
    "Sagen Sie uns kurz, worum es in Ihrer Anfrage geht und was Sie besprechen möchten.";
  const introText =
    publicForm?.intro_text?.trim() ||
    "Wir melden uns zeitnah bei Ihnen und bereiten das Gespräch passend zu Ihrem Anwendungsfall vor. Eine kurze Einordnung genügt - Details klären wir gemeinsam.";
  const buttonText = publicForm?.button_text?.trim() || "Anfrage senden";
  const thankYouTitle = publicForm?.thank_you_title?.trim() || "Vielen Dank für Ihre Anfrage";
  const thankYouBody =
    publicForm?.thank_you_text?.trim() ||
    `Wir haben Ihre Anfrage zu ${safeProject} erhalten. Unser Team prüft Ihre Angaben und meldet sich zeitnah mit den nächsten Schritten.`;
  const privacyCheckboxText =
    publicForm?.privacy_checkbox_text?.trim() ||
    "Hiermit stimme ich der Speicherung und Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. Diese Einwilligung kann ich jederzeit per E-Mail widerrufen.\nHinweis: Hier kann unsere Datenschutzerklärung abgerufen werden.";
  const emailTrackingConsentEnabled = Boolean(publicForm?.email_tracking_consent_enabled);
  const emailTrackingConsentText =
    publicForm?.email_tracking_consent_text?.trim() ||
    "Ich bin damit einverstanden, dass Öffnungen und Klicks der im Zusammenhang mit meiner Anfrage versendeten E-Mails gemessen werden, um die Kommunikation und Beratung zu verbessern. Diese Einwilligung ist freiwillig und kann jederzeit mit Wirkung für die Zukunft per E-Mail widerrufen werden.";
  const marketingConsentEnabled = Boolean(publicForm?.marketing_consent_enabled);
  const marketingConsentText =
    publicForm?.marketing_consent_text?.trim() ||
    "Ich möchte gelegentlich Informationen zu Produkten, Fachinhalten und passenden Angeboten per E-Mail erhalten. Ich kann diese Einwilligung jederzeit widerrufen.";
  const privacyPolicyUrl = publicForm?.privacy_policy_url?.trim() || "";
  const imprintUrl = publicForm?.imprint_url?.trim() || "";
  const legalNoticeText = publicForm?.legal_notice_text?.trim() || "";
  const showLegalNotice = Boolean(legalNoticeText);
  const contactEmail = tenantInfo?.contact_email?.trim() || "";
  const isAvailable =
    Boolean(publicForm?.is_enabled) &&
    isValidPublicHttpUrl(privacyPolicyUrl) &&
    isValidPublicHttpUrl(imprintUrl);

  return {
    kicker: `Öffentliche Anfrage · ${safeProject}`,
    headline,
    subline,
    introText,
    fieldConfig,
    interestLabel: "Was möchten Sie besprechen?",
    interestHelpText:
      "Damit wir Ihr Anliegen gezielt einordnen und das Gespräch gut vorbereiten können.",
    messagePlaceholder: "Worum geht es in Ihrer Anfrage oder was möchten Sie besprechen?",
    buttonText,
    privacyCheckboxText,
    emailTrackingConsentEnabled,
    emailTrackingConsentText,
    marketingConsentEnabled,
    marketingConsentText,
    privacyDisclaimerParts: buildPrivacyDisclaimerParts(privacyCheckboxText, {
      contactEmail,
      privacyPolicyUrl,
    }),
    privacyPolicyUrl,
    imprintUrl,
    contactEmail,
    legalNoticeHeading: "Rechtlicher Hinweis",
    legalNoticeText,
    showLegalNotice,
    thankYouTitle,
    thankYouBody,
    thankYouCta: "Zurück zur Anfrage",
    isAvailable,
  };
}

export function getPublicThankYouProject(tenantInfo) {
  return tenantInfo?.default_project || "OptiMES Prime";
}
